import { useContext, useState, useCallback, useEffect } from 'react';
import classNames from './NotificationsList.module.scss';
import { useTranslation } from 'react-i18next';
import { NotificationsContext } from '@/context/NotificationsContext';
import SVG from 'react-inlinesvg';
import bellIcon from '@/assets/svg/bell.svg';
import { NotificationStatus, SetNotificationsRequest } from '@/types/services/notification';
import NotificationRow from './NotificationRow';

const API_DEBOUNCE_MS = 3000;

interface NotificationsListProps {}

const NotificationsList = ({}: NotificationsListProps) => {
  const { t } = useTranslation();
  const { numberOfUnreadNotifications, notifications, setAllAsRead, setNotificationStatus } =
    useContext(NotificationsContext);
  const [pendingIds, setPendingIds] = useState<string[]>([]);

  const syncReadStatusToApi = useCallback(
    (ids: string[]) => {
      const unreadIdsInState = new Set(
        notifications.filter(n => n.status === NotificationStatus.Unread).map(n => n.notificationId)
      );

      const seenInBatch = new Set<string>();
      const updates: SetNotificationsRequest[] = [];

      ids.forEach(id => {
        if (unreadIdsInState.has(id) && !seenInBatch.has(id)) {
          seenInBatch.add(id);
          updates.push({
            notificationId: id,
            status: NotificationStatus.Read,
          });
        }
      });

      if (updates.length > 0) {
        setNotificationStatus(updates);
      }

      setPendingIds([]);
    },
    [notifications, setNotificationStatus]
  );

  useEffect(() => {
    if (pendingIds.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      syncReadStatusToApi(pendingIds);
    }, API_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [pendingIds, syncReadStatusToApi]);

  const handleMarkAsSeen = useCallback((id: string) => {
    setPendingIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const handleDelete = useCallback(
    (id: string) => {
      setPendingIds(prev => prev.filter(pId => pId !== id));

      setNotificationStatus([
        {
          notificationId: id,
          status: NotificationStatus.Deleted,
        },
      ]);
    },
    [setNotificationStatus]
  );

  const handleMarkAll = useCallback(() => {
    if (numberOfUnreadNotifications > 0) {
      setPendingIds([]);
      setAllAsRead();
    }
  }, [setAllAsRead, numberOfUnreadNotifications]);

  return (
    <div className={classNames.container}>
      <div className={classNames.header}>
        <div className={classNames.headerStart}>
          <div className={classNames.bellIcon}>
            <SVG src={bellIcon} />
          </div>
          <div className={classNames.headerTitle}>{t('notifications.list.title')}</div>
        </div>
        <div className={classNames.headerEnd}>
          <button
            className={classNames.headerActionButton}
            onClick={handleMarkAll}
            disabled={numberOfUnreadNotifications === 0}
          >
            {t('notifications.list.markAllAsReadButton')}
          </button>
        </div>
      </div>
      <div className={classNames.body}>
        <div className={classNames.list}>
          {notifications.map(n => {
            return (
              <div key={`notification--${n.notificationId}`}>
                <NotificationRow
                  notification={n}
                  onDeleted={handleDelete}
                  onMarkAsSeen={handleMarkAsSeen}
                />
              </div>
            );
          })}
        </div>
        <div className={classNames.listMessage}>
          {t(
            notifications.length > 0
              ? 'notifications.list.allNotificationsMessage'
              : 'notifications.list.noNotificationsMessage'
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsList;
